import language from '@/config/language';
import {
    getPlatformLogo,
    isArivalPlatform,
    isGlobalBlockPlatform,
    isPaytechPlatform,
    isRoundedPlatform,
    isWolfBTCPlatform,
    logoTitle,
    portalTitle,
    spotTitle,
    traderTitle,
    xplorSpotLiteTitle,
    xplorSpotTitle,
    xplorTraderTitle
} from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType, selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { selectLanguage } from '@/state/reducers/languageSlice';
import { showLayoutSwitch } from '@/utils/route';
import cn from 'classnames';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    LuActivity,
    LuArrowDownToLine,
    LuArrowUpFromLine,
    LuBarChart3,
    LuLogOut,
    LuPieChart,
    LuSettings,
    LuSliders,
    LuTrendingUp,
    LuZap
} from 'react-icons/lu';
import { MdOutlineAccountBalance, MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { onLogout } from './Navbar';
import { SidebarItem } from './SidebarItem';
import { SidebarPlatforms } from './SidebarPlatforms';

export interface PathProps {
    label: string;
    Icon: IconType;
    path: string;
    permissions: string[];
    onClick?: () => void;
    key?: string;
}

const spot_logo = window.config?.logo;
const lite_logo = window.config?.lite_logo;
const trader_logo = window.config?.trader_logo;
const portal_logo = window.config?.portal_logo;

function UniversalSidebar({ settingsDisclosure, isExpanded, setIsExpanded, isAlwaysExpanded }: any) {
    const dispatch = useAppDispatch();

    const pathname = location.pathname.replace(/\/$/, '');

    const lang = useAppSelector(selectLanguage);
    const accountType = useAppSelector(selectAccountType);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const currentAccountType = useMemo(() => {
        const properties = credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [];
        return properties.find((property) => property.key === 'TYPE')?.value || 'NOP';
    }, [credentials, currentAccount]);

    const asideProps = useMemo(() => {
        if (isAlwaysExpanded) {
            return {
                // isExpanded: true
            };
        }
        return {
            // isExpanded,
            onMouseEnter: () => setIsExpanded(true),
            onMouseLeave: () => setIsExpanded(false)
        };
    }, [isAlwaysExpanded, isExpanded]);

    const accountItems: PathProps[] = useMemo(() => {
        let items: PathProps[] = [
            {
                Icon: LuSettings,
                key: 'Settings',
                label: language[lang].navigation['settings'],
                path: '/settings',
                permissions: [],
                onClick: () => settingsDisclosure[1].toggle()
            },
            {
                Icon: LuLogOut,
                key: 'Logout',
                label: language[lang].navigation['logout'],
                path: '/logout',
                permissions: [],
                onClick: () => onLogout(credentials, dispatch)
            }
        ];

        if (pathname.includes('portal')) items = items.filter((p) => p.key !== 'Settings');

        return items;
    }, [credentials, pathname, lang, settingsDisclosure, dispatch]);

    const platformItems: PathProps[] = useMemo(() => {
        let platforms = [
            {
                Icon: LuTrendingUp,
                label: xplorSpotTitle,
                path: '/',
                permissions: [],
                key: 'XplorSpot'
            },
            {
                Icon: LuZap,
                label: xplorSpotLiteTitle,
                path: '/lite',
                permissions: [],
                key: 'XplorSpotLite'
            },
            {
                Icon: LuBarChart3,
                label: xplorTraderTitle,
                path: '/trader',
                permissions: [],
                key: 'XplorTrader'
            }
        ];

        if (pathname.includes('portal')) {
            platforms = platforms.filter((p) => p.key !== 'XplorPortal');
            if (currentAccountType === 'NOP') {
                platforms = platforms.filter((p) => p.key !== 'XplorTrader');
            } else if (currentAccountType === 'MARGIN') {
                platforms = platforms.filter((p) => p.key === 'XplorTrader');
            }
        } else if (pathname.includes('trader')) {
            platforms = platforms.filter((p) => p.key === 'XplorTrader');
        } else if (pathname.includes('lite')) {
            platforms = platforms.filter((p) => ['XplorSpot', 'XplorSpotLite'].includes(p.key));
        } else if (pathname === '') {
            platforms = platforms.filter((p) => ['XplorSpotLite', 'XplorSpot'].includes(p.key));
        }

        return platforms;
    }, [currentAccountType, pathname]);

    const portalItems: PathProps[] = useMemo(() => {
        const platforms = [
            {
                key: 'Overview',
                label: language[lang].navigation['overview'],
                Icon: LuPieChart,
                path: '/portal',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Deposits',
                label: language[lang].navigation['deposits'],
                Icon: LuArrowDownToLine,
                path: '/portal/deposits',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_DEPOSIT']
            },
            {
                key: 'Withdrawals',
                label: language[lang].navigation['withdrawals'],
                Icon: LuArrowUpFromLine,
                path: '/portal/withdrawals',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WITHDRAWAL']
            },
            {
                key: 'Trades',
                label: language[lang].navigation['trades'],
                Icon: LuActivity,
                path: '/portal/trades',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Instruments',
                label: language[lang].navigation['instruments'],
                Icon: LuSliders,
                path: '/portal/instruments',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Bank Accounts',
                label: language[lang].navigation['bank accounts'],
                Icon: MdOutlineAccountBalance,
                path: '/portal/bank-accounts',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST']
            },
            {
                key: 'Wallets',
                label: language[lang].navigation['wallets'],
                Icon: MdOutlineAccountBalanceWallet,
                path: '/portal/wallets',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST']
            }
        ];

        return platforms.filter((platform) => {
            if (accountType === 'MARGIN') return platform.key !== 'Wallets';
            return true;
        });
    }, [pathname, accountType, lang]);

    const logo_src = useMemo(() => {
        if (pathname.includes('/lite')) return lite_logo;
        else if (pathname.includes('/trader')) return trader_logo;
        else if (pathname.includes('/portal')) return portal_logo;
        else return spot_logo;
    }, [pathname]);

    const collapsed_logo_src = useMemo(() => {
        if (isPaytechPlatform) return '/config/paytech_logo_only.png';
        else if (isArivalPlatform) return '/favicon-arival.ico';
        else if (isWolfBTCPlatform) return '/config/wolfbtc_logo_only.png';
        else if (isGlobalBlockPlatform) return '/config/globalblock_logo_only.png';
        else return '/favicon-gcex.ico';
    }, []);

    const logo_alt = useMemo(() => {
        if (pathname.includes('/lite')) return spotTitle;
        else if (pathname.includes('/trader')) return traderTitle;
        else if (pathname.includes('/portal')) return portalTitle;
        else return spotTitle;
    }, [pathname]);

    return (
        <aside
            {...asideProps}
            className={cn(
                `hidden lg:flex flex-col fixed left-0 top-0 z-[51] w-full bg-brand-background h-screen transition-all duration-200 ease-in-out border-r-[3px] border-brand-primary text-brand-neutral-200`,
                {
                    'max-w-[260px]': isExpanded,
                    'max-w-16': !isExpanded
                }
            )}>
            {/* Logo Section */}
            <div className="relative px-6 md:min-h-[67px] border-b-[3px] border-brand-primary flex items-center gap-2">
                <img
                    className={cn(
                        'absolute inset-0 m-auto h-14 w-fit cursor-pointer transition-all duration-200 overflow-hidden',
                        {
                            'opacity-100 w-auto': !isExpanded,
                            'opacity-0 w-0': isExpanded,
                            'p-2': isPaytechPlatform || isArivalPlatform || isWolfBTCPlatform || isGlobalBlockPlatform
                        }
                    )}
                    src={collapsed_logo_src}
                    alt={logo_alt}
                />

                {isRoundedPlatform && (
                    <div className="flex flex-row h-full items-center justify-center gap-3 w-full">
                        <div className="flex flex-col py-2 h-full max-h-12 w-full relative mb-0.5">
                            <img className="block max-h-full max-w-[88.75px]" src={getPlatformLogo()} alt={logoTitle} />
                            {isArivalPlatform && (
                                <div className="text-2xs absolute bottom-0.5 left-8 text-brand-primary-darker">
                                    Powered by GCEX
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!isRoundedPlatform && !(isPaytechPlatform || isWolfBTCPlatform || isGlobalBlockPlatform) && (
                    <img
                        className={cn('block h-10 w-fit transition-all duration-200 overflow-hidden', {
                            'opacity-100 w-auto': isExpanded,
                            'opacity-0 w-0': !isExpanded
                        })}
                        src={logo_src}
                        alt={logo_alt}
                    />
                )}
                {(isPaytechPlatform || isWolfBTCPlatform || isGlobalBlockPlatform) && (
                    <div className="flex flex-row h-full items-center justify-center gap-3 w-full">
                        <div className="flex flex-col h-full max-h-16 py-2 w-full relative">
                            <img
                                className={cn('block w-fit transition-all duration-200 overflow-hidden', {
                                    'opacity-100 w-auto': isExpanded,
                                    'opacity-0 w-0': !isExpanded
                                })}
                                src={logo_src}
                                alt={logo_alt}
                            />
                        </div>
                    </div>
                )}
            </div>
            {/* Scrollable Navigation Section */}
            <div
                className={cn('flex-1 overflow-y-auto overflow-x-hidden flex flex-col', {
                    'p-5 pb-6 px-4 gap-6': isExpanded,
                    'p-2 gap-4': !isExpanded
                })}>
                {/* Actions Group */}
                <div>
                    {isExpanded && (
                        <div className="px-4 mb-2">
                            <span className="text-xs font-semibold text-brand-neutral-500 uppercase tracking-wider">
                                {portalTitle}
                            </span>
                        </div>
                    )}
                    <div className="space-y-1">
                        {portalItems.map(({ key, ...item }) => (
                            <SidebarItem key={key} isExpanded={isExpanded} {...item} />
                        ))}
                    </div>
                </div>
                {/* Platforms Group */}
                {showLayoutSwitch && platformItems.length > 0 && (
                    <div>
                        {isExpanded && (
                            <div className="px-4 mb-2">
                                {/* TBA - Translations */}
                                <span className="text-xs font-semibold text-brand-neutral-500 uppercase tracking-wider whitespace-nowrap">
                                    Trading Platforms
                                </span>
                            </div>
                        )}
                        <div
                            className={cn('', {
                                'space-y-4': !isExpanded,
                                'space-y-1': isExpanded
                            })}>
                            {platformItems.map(({ key, ...item }) => (
                                <SidebarPlatforms key={key} isExpanded={isExpanded} {...item} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Account Section (Fixed Bottom) */}
            <div
                className={cn('border-t-[3px] border-brand-primary py-3 px-4 flex flex-col gap-1', {
                    'md:min-h-32': !pathname.includes('portal')
                })}>
                {accountItems.map(({ key, ...item }) => (
                    <SidebarItem key={key} isExpanded={isExpanded} {...item} />
                ))}
            </div>
        </aside>
    );
}

export default UniversalSidebar;
